/* globals window */
import React, { useEffect, useState } from 'react';
import {
  setSidebarVisibility,
  useRedirect,
  useRefresh,
  useNotify,
  Sidebar,
  Notification,
} from 'react-admin';

import { TourProvider } from '@react-admin/ra-tour';

import useScrollTrigger from '@material-ui/core/useScrollTrigger/useScrollTrigger';
import Slide from '@material-ui/core/Slide';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from '@material-ui/core';
import Menu from './Menu';
import MyAppBar from './MyAppBar';
import customThemes from './theme';
import QuickActions from './QuickActions';
import QuickAppointmentProvider from './Context/QuickAppointmentProvider';
import QuickQuoteProvider from './Context/QuickQuoteProvider';
import zippityTour from './tours/ZippityTour';
import './index.css';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1,
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    position: 'relative',
    minWidth: 'fit-content',
    width: '100%',
  },
  appFrame: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('xs')]: {
      marginTop: theme.spacing(6),
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(7),
    },
    // Improve print previews and PDFs
    '@media print': {
      marginTop: 0,
    },
  },
  contentWithSidebar: {
    display: 'flex',
    flexGrow: 1,
    position: 'relative',
    top:15
  },
  sidebar: {
    position: 'fixed',
    // backgroundColor: theme.sidebar.background,
    overflowX: 'hidden',
    overflowY: 'auto',
    height: '100vh',
    zIndex: 3,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexBasis: 0,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingLeft: 0,
    [theme.breakpoints.up('xs')]: {
      paddingLeft: 5,
    },
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  sidebarOpen: { paddingLeft: '260px' },
  sidebarClosed: { paddingLeft: '30px' },
  pullToRefresh: {
    width: '100vw',
    textAlign: 'center',
    position: 'fixed',
  },
}));

const MyLayout = (props) => {
  // "Pull to Refresh":
  // When you pull down (to scroll up past the top) on a mobile device,
  // show a circular spinner and refresh the data on the page.
  // (A negative `threshold` value is above the "top" of the page.)
  // https://material-ui.com/components/app-bar/#scrolling
  const refresh = useRefresh();
  const notify = useNotify();
  const redirect = useRedirect();
  const isFromS360 = window?.location.ancestorOrigins?.[0]?.includes('simple360') || window?.location.href.includes('fromS360');
  const scrollTrigger = useScrollTrigger({ threshold: -25 });
  const negativeY = window ? window.scrollY < 0 : false;
  const shouldRefresh = negativeY && !scrollTrigger;
  if (shouldRefresh) {
    console.log(`Refresh: ${window.location}`);
    refresh();
  }
  const { children, title, logout } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const open = useSelector((state) => state.admin.ui.sidebarOpen);
  const theme = useSelector((state) => state.settings.theme);
  const isXSmall = useMediaQuery(customThemes[theme].breakpoints.down('xs'));
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);

  useEffect(() => {
    dispatch(setSidebarVisibility(true && !isXSmall));
  }, [dispatch, isXSmall]);

  return (
    <TourProvider
      tours={zippityTour}
      joyrideProps={{ showSkipButton: false, locale: { last: 'Finish' } }}
      tools={{ notify, redirect, setSelectedMenuItem }}
    >
      <MuiThemeProvider theme={customThemes[theme]}>
        <QuickQuoteProvider>
          <QuickAppointmentProvider>
            <div
              className={`${classes.root} ${
                open ? 'sidebar-open' : 'sidebar-close'
              }`}
            >
              <div className={classes.appFrame} style={isFromS360 ? { marginTop: 0 } : {}}>
                {!isFromS360 && <MyAppBar title={title} open={open} logout={logout} />}
                <main className={classes.contentWithSidebar}>
                  {!isFromS360 && <Sidebar
                    className={`drawerRoot ${classes.sidebar}`}
                    style={{
                      backgroundColor: customThemes[theme].sidebar.background,
                      height: open ? '100%' : '100vh',
                    }}
                    id="sidebar"
                  >
                    <Menu
                      logout={logout}
                      selectedMenuItem={selectedMenuItem}
                      setSelectedMenuItem={setSelectedMenuItem}
                      hasDashboard
                    />
                  </Sidebar>}
                  <div className={classes.pullToRefresh}>
                    <Slide in={shouldRefresh}>
                      <CircularProgress />
                    </Slide>
                  </div>
                  <div
                    className={`${classes.content} sidebar`}
                    style={isFromS360 ? {} : {
                      paddingLeft: open ? '260px' : '70px',
                    }}
                  >
                    {children}
                  </div>
                </main>
                <Notification />
              </div>
            </div>
            <QuickActions />
          </QuickAppointmentProvider>
        </QuickQuoteProvider>
      </MuiThemeProvider>
    </TourProvider>
  );
};

export default MyLayout;
